/** A melléklet tartalom-API-ja: típusok + TanStack Query hookok (rendszerterv 8.). */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { api } from "./client";

export type ModerationStatus = "pending" | "approved" | "rejected";
export type ExerciseKind = "exercise" | "meditation";
export type Attitude = "tudatosito" | "tanulmanyozo" | "onfejleszto" | "alkoto";
export type MarkStatus = "want_to_try" | "tried" | "not_for_me";
/** Az ajánló személyes jelölése: tervezem / megnéztem / kihagyom (a kedvenc külön flag). */
export type ListStatus = "saved" | "done" | "skipped";
/** Kitől jön az ajánlás: a szerzőktől vagy a közösségtől. */
export type ReadingSource = "authors" | "community";
export type Language = "hu" | "en";

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

export interface ChapterRef {
  id: number;
  number: string;
  title: string;
}

export interface ChapterCounts {
  exercises: number;
  glossary: number;
  experiences: number;
  errata: number;
}

export interface Chapter extends ChapterRef {
  lead: string | null;
  parent_id: number | null;
  counts: ChapterCounts;
  children: Chapter[];
}

export interface ExerciseMark {
  status: MarkStatus | null;
  is_favorite: boolean;
  note: string | null;
}

export interface Exercise {
  id: number;
  title: string;
  body: string;
  kind: ExerciseKind;
  attitude: Attitude | null;
  needs_ai: boolean;
  ai_prompt: string | null;
  audio_url: string | null;
  audio_seconds: number | null;
  /** Egy gyakorlat több fejezethez is tartozhat. */
  chapters: ChapterRef[];
  mark: ExerciseMark;
  experience_count: number;
}

export interface ItemType {
  id: number;
  name: string;
}

/** Az ajánlás indexképe (saját másolat a `/media` alól) a tárolt mérettel — a kártya az
 *  arányból dönti el, kitölti-e a 16:9-es fedlapot, vagy elmosott háttér elé teszi. */
export interface Thumbnail {
  url: string;
  width: number;
  height: number;
}

export type EmbedProvider = "youtube";

/** A link felismert szolgáltatója és a beágyazó lejátszó címe. Az `url` üres a
 *  YouTube-csatornánál (annak csak képe és linkje van); a lejátszási lista a videóhoz hasonlóan
 *  lejátszható. A `handle` a csatorna `@kezelőneve`, ha a link ilyen. */
export interface Embed {
  provider: EmbedProvider;
  kind: "video" | "channel" | "playlist";
  url: string | null;
  handle: string | null;
}

export interface ReadingItem {
  id: number;
  title: string;
  body: string;
  url: string | null;
  item_type: ItemType | null;
  language: Language;
  published_year: number | null;
  /** „Miért ajánlod, mit adott neked?" — Markdown. */
  recommend_note: string | null;
  /** Szerző / alkotó (a könyv szerzője, a film rendezője…) — a cím alatt. */
  creator: string | null;
  /** Alcím — a kártya nem mutatja (kereshető, az adminban szerkeszthető). */
  subtitle: string | null;
  /** Eredeti cím, ha a mutatott kiadás fordítás — a kártyán „Eredeti cím: …”. */
  original_title: string | null;
  source: ReadingSource;
  /** A leírás AI-jal készült összefoglaló. */
  ai_summary: boolean;
  is_pinned: boolean;
  status: ModerationStatus;
  /** Közösségi ajánlásnál a beküldő neve (null, ha a fiók törlődött); a szerzőinél mindig null. */
  author: string | null;
  mine: boolean;
  list_status: ListStatus | null;
  is_favorite: boolean;
  created_at: string;
  thumbnail: Thumbnail | null;
  embed: Embed | null;
}

export interface Experience {
  id: number;
  author: string;
  body: string;
  created_at: string;
  book_consent: boolean;
  like_count: number;
  liked_by_me: boolean;
  chapters: ChapterRef[];
  exercises: ChapterRef[];
  status: ModerationStatus;
  mine: boolean;
  parent_id: number | null;
  replies: Experience[];
  reply_count: number;
}

export interface GlossaryTerm {
  id: number;
  terms_hu: string;
  term_en: string | null;
  body: string;
  chapters: ChapterRef[];
}

export interface Erratum {
  id: number;
  chapters: ChapterRef[];
  locator: string | null;
  body: string;
  status: ModerationStatus;
  mine: boolean;
  from_authors: boolean;
  created_at: string;
}

export interface Review {
  id: number;
  body: string;
  public_ok: boolean;
  show_on_book_page: boolean;
  status: ModerationStatus;
  created_at: string;
}

export interface ChapterDetail {
  chapter: Chapter;
  prev: ChapterRef | null;
  next: ChapterRef | null;
  exercises: Exercise[];
  glossary: GlossaryTerm[];
  experiences: Experience[];
  errata: Erratum[];
}

export interface DailyPick {
  day: string;
  exercise: Exercise | null;
  exhausted: boolean;
}

export type SearchKind = "exercise" | "reading" | "experience" | "glossary" | "erratum";

export interface SearchHit {
  kind: SearchKind;
  id: number;
  title: string;
  snippet: string;
  chapters: ChapterRef[];
}

/** Lekérdezés-paraméterek query stringgé; az üres értékek kimaradnak. */
export function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    sp.set(key, String(value));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const keys = {
  chapters: ["chapters"] as const,
  chapter: (id: number) => ["chapter", id] as const,
  exercises: (params: unknown) => ["exercises", params] as const,
  exercise: (id: number) => ["exercise", id] as const,
  daily: ["daily"] as const,
  readings: (params: unknown) => ["readings", params] as const,
  itemTypes: ["item-types"] as const,
  experiences: (params: unknown) => ["experiences", params] as const,
  glossary: (params: unknown) => ["glossary", params] as const,
  errata: (params: unknown) => ["errata", params] as const,
  reviews: ["reviews", "mine"] as const,
  search: (q: string) => ["search", q] as const,
};

// --- Fejezetek --------------------------------------------------------------

export const useChapters = () =>
  useQuery({ queryKey: keys.chapters, queryFn: () => api<Chapter[]>("/book/chapters") });

export const useChapter = (id: number) =>
  useQuery({
    queryKey: keys.chapter(id),
    queryFn: () => api<ChapterDetail>(`/book/chapters/${id}`),
    enabled: Number.isFinite(id),
  });

// --- Gyakorlattár -----------------------------------------------------------

export interface ExerciseQuery {
  chapter_id?: number;
  attitude?: Attitude | "";
  kind?: ExerciseKind | "";
  needs_ai?: boolean;
  /** `none` = még nem jelöltem (nincs állapota; a kedvenc és a jegyzet ettől független). */
  mark?: MarkStatus | "favorite" | "noted" | "none" | "";
  q?: string;
  page?: number;
}

export const useExercises = (params: ExerciseQuery) =>
  useQuery({
    queryKey: keys.exercises(params),
    queryFn: () => api<Page<Exercise>>(`/book/exercises${qs(params as Record<string, unknown>)}`),
  });

export const useExercise = (id: number) =>
  useQuery({
    queryKey: keys.exercise(id),
    queryFn: () => api<Exercise>(`/book/exercises/${id}`),
    enabled: Number.isFinite(id),
  });

export function useSaveMark(exerciseId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<ExerciseMark>) =>
      api<Exercise>(`/book/exercises/${exerciseId}/mark`, { method: "PUT", body }),
    onSuccess: (exercise) => {
      qc.setQueryData(keys.exercise(exerciseId), exercise);
      void qc.invalidateQueries({ queryKey: ["exercises"] });
    },
  });
}

export const useDailyPick = (options?: Partial<UseQueryOptions<DailyPick>>) =>
  useQuery({
    queryKey: keys.daily,
    queryFn: () => api<DailyPick>("/book/exercises/daily"),
    ...options,
  });

export function useDrawDaily() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<DailyPick>("/book/exercises/daily", { method: "POST" }),
    onSuccess: (pick) => qc.setQueryData(keys.daily, pick),
  });
}

// --- Olvasnivalók -----------------------------------------------------------

export interface ReadingQuery {
  source?: ReadingSource;
  item_type_id?: number;
  language?: Language | "";
  /** Az ajánlások globálisak — fejezet-szűrő nincs; a jelöléseimre lehet szűrni.
   *  `none` = még nem jelöltem (nincs állapota; a kedvenc ettől független). */
  on_list?: ListStatus | "favorite" | "none" | "any" | "";
  q?: string;
  page?: number;
}

/** A lista lapja + forrásonkénti darabszám a fülekhez (a forrás-szűrőt kivéve minden szűrőt
 *  és a keresést is követi). */
export interface ReadingPage extends Page<ReadingItem> {
  source_counts: Record<ReadingSource, number>;
}

export const useReadings = (params: ReadingQuery) =>
  useQuery({
    queryKey: keys.readings(params),
    queryFn: () => api<ReadingPage>(`/book/readings${qs(params as Record<string, unknown>)}`),
    // fülváltáskor és szűréskor az előző lap marad kint, amíg az új meg nem jön
    placeholderData: (prev) => prev,
  });

export const useItemTypes = () =>
  useQuery({
    queryKey: keys.itemTypes,
    queryFn: () => api<ItemType[]>("/book/item-types"),
    staleTime: 10 * 60 * 1000,
  });

export interface ReadingInput {
  title: string;
  body: string;
  url: string | null;
  item_type_id: number | null;
  language: Language;
  published_year: number | null;
  recommend_note: string | null;
  creator: string | null;
}

export function useSubmitReading() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ReadingInput) =>
      api<ReadingItem>("/book/readings", { method: "POST", body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["readings"] }),
  });
}

/** Olvasólista-állapot és kedvenc jelölés; a meg nem adott mező változatlan marad. */
export function useReadingMark() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; status?: ListStatus | null; is_favorite?: boolean }) =>
      api<ReadingItem>(`/book/readings/${id}/mark`, { method: "PUT", body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["readings"] }),
  });
}

// --- Tapasztalatok ----------------------------------------------------------

export interface ExperienceQuery {
  chapter_id?: number;
  exercise_id?: number;
  /** „mine" = csak a sajátjaim, „liked" = amiket kedveltem. */
  who?: "mine" | "liked";
  sort?: "new" | "liked";
  q?: string;
  page?: number;
  /** Beágyazott listáknál (gyakorlat-, fejezet-oldal) rövidebb oldalhossz. */
  page_size?: number;
}

export const useExperiences = (params: ExperienceQuery) =>
  useQuery({
    queryKey: keys.experiences(params),
    queryFn: () => api<Page<Experience>>(`/book/experiences${qs(params as Record<string, unknown>)}`),
  });

export interface ExperienceInput {
  body: string;
  book_consent: boolean;
  chapter_ids: number[];
  exercise_ids: number[];
  parent_id?: number | null;
}

export function useSubmitExperience() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ExperienceInput) =>
      api<Experience>("/book/experiences", { method: "POST", body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["experiences"] }),
  });
}

export function useToggleLike() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, liked }: { id: number; liked: boolean }) =>
      api<Experience>(`/book/experiences/${id}/like`, { method: liked ? "DELETE" : "POST" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["experiences"] }),
  });
}

// --- Fogalomtár, hibajegyzék, vélemények ------------------------------------

export const useGlossary = (params: { q?: string; chapter_id?: number; page?: number }) =>
  useQuery({
    queryKey: keys.glossary(params),
    queryFn: () => api<Page<GlossaryTerm>>(`/book/glossary${qs(params)}`),
  });

export const useErrata = (params: { q?: string; chapter_id?: number; page?: number }) =>
  useQuery({
    queryKey: keys.errata(params),
    queryFn: () => api<Page<Erratum>>(`/book/errata${qs(params)}`),
  });

export function useSubmitErratum() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { body: string; chapter_ids: number[]; locator: string | null }) =>
      api<Erratum>("/book/errata", { method: "POST", body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["errata"] }),
  });
}

export const useMyReviews = () =>
  useQuery({ queryKey: keys.reviews, queryFn: () => api<Review[]>("/book/reviews/mine") });

export interface ReviewInput {
  body: string;
  public_ok: boolean;
}

/** Új vélemény, vagy `id` esetén a saját vélemény módosítása (utóbbi újra moderációra kerül). */
export function useSubmitReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ReviewInput & { id?: number }) =>
      id
        ? api<Review>(`/book/reviews/${id}`, { method: "PUT", body })
        : api<Review>("/book/reviews", { method: "POST", body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.reviews }),
  });
}

export function useDeleteReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api<void>(`/book/reviews/${id}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.reviews }),
  });
}

export const useSearch = (q: string) =>
  useQuery({
    queryKey: keys.search(q),
    queryFn: () => api<{ q: string; hits: SearchHit[] }>(`/book/search${qs({ q })}`),
    enabled: q.trim().length >= 2,
  });
